import React, { createContext, useContext, useMemo, useState } from 'react';
import Api from 'talent_search/lib/api';
import type {
  Paths,
  SavedSearchGroup,
  SavedSearchItem,
} from 'talent_search/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { RediscoveryProps } from 'talent_matching/types';

export interface RediscoveryTabContextValue {
  apiClient: Api;
  paths: Paths;
  milestones: Item[];
  currencies: Item[];
  savedSearchOptions: SavedSearchGroup[];
  unfilteredCount: number;
  similarJobOptions: Item[];
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (ss: SavedSearchItem | null) => void;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
}

const RediscoveryTabContext = createContext<
  RediscoveryTabContextValue | undefined
>(undefined);

export const useRediscoveryTabContext = () => {
  const context = useContext(RediscoveryTabContext);
  if (!context) {
    throw new Error(
      'useRediscoveryTabContext must be used within a RediscoveryTabContextProvider'
    );
  }
  return context;
};

type RediscoveryTabContextProviderProps = {
  rediscoveryProps: RediscoveryProps;
  children: React.ReactNode;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
};

export const RediscoveryTabContextProvider = ({
  rediscoveryProps,
  children,
  activeSidePanelTab,
  setActiveSidePanelTab,
}: RediscoveryTabContextProviderProps) => {
  const [savedFilterSet, setSavedFilterSet] = useState<SavedSearchItem | null>(
    null
  );

  const apiClient = useMemo(() => {
    return new Api(rediscoveryProps.paths);
  }, [rediscoveryProps?.paths]);

  const contextValue: RediscoveryTabContextValue = useMemo(
    () => ({
      apiClient,
      paths: rediscoveryProps.paths,
      milestones: rediscoveryProps.milestones,
      currencies: rediscoveryProps.currencies,
      savedSearchOptions: rediscoveryProps.savedSearchOptions ?? [],
      unfilteredCount: rediscoveryProps.unfilteredCount,
      similarJobOptions: rediscoveryProps.similarJobOptions ?? [],
      savedFilterSet,
      setSavedFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
    }),
    [
      rediscoveryProps,
      apiClient,
      savedFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
    ]
  );

  return (
    <RediscoveryTabContext.Provider value={contextValue}>
      {children}
    </RediscoveryTabContext.Provider>
  );
};
